import { useNavigate } from "react-router-dom";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Pencil, Plus, Trash2 } from "lucide-react";
import type { ColumnDef } from "@tanstack/react-table";
import { toast } from "@/components/ui/sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { DataTable, type RowAction } from "@/components/data-table/DataTable";
import { usePagedQuery } from "@/components/data-table/usePagedQuery";
import { deleteUsuario, USUARIO_PATH, type Usuario, type UsuarioFiltro } from "./api";

// Porte de app/controllers/consulta-usuario.controller.js +
// app/templates/usuario/consulta-usuario.html. Diferente do TipoArquivo:
// "Adicionar Novo"/"Editar" navegam pra uma rota separada (/Usuario/Form), não
// abrem modal — o legado já fazia assim (`$location.url("Usuario/Form...")`).
const QUERY_KEY = ["usuario"];

const columns: ColumnDef<Usuario, unknown>[] = [
  { accessorKey: "id", header: "Código", enableSorting: true },
  { accessorKey: "nome", header: "Nome", enableSorting: true },
  { accessorKey: "email", header: "Email", enableSorting: true },
  { accessorKey: "usuario", header: "Usuário", enableSorting: true },
];

export default function ConsultaUsuario() {
  const navigate = useNavigate();
  const qc = useQueryClient();

  const query = usePagedQuery<Usuario, UsuarioFiltro>({
    key: QUERY_KEY,
    path: USUARIO_PATH,
    initialFiltro: { tipoUsuarioIn: ["SILPI"] },
  });

  const excluir = useMutation({
    mutationFn: deleteUsuario,
    onSuccess: () => {
      toast.success("Usuário excluído.");
      qc.invalidateQueries({ queryKey: QUERY_KEY });
    },
  });

  const actions: RowAction<Usuario>[] = [
    { label: "Editar", icon: Pencil, onClick: (row) => navigate(`/Usuario/Form/${row.id}`) },
    {
      label: "Excluir",
      icon: Trash2,
      variant: "destructive",
      onClick: (row) => excluir.mutate(row.id),
      confirm: {
        title: "Excluir usuário?",
        description: (row) => `"${row.nome}" será removido permanentemente.`,
        confirmLabel: "Excluir",
      },
    },
  ];

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h1 className="text-lg font-semibold text-foreground">Usuários</h1>
        <Button onClick={() => navigate("/Usuario/Form")}>
          <Plus className="mr-2 h-4 w-4" /> Adicionar Novo
        </Button>
      </div>

      <DataTable
        query={query}
        columns={columns}
        actions={actions}
        getRowId={(r) => r.id}
        exportFilename="usuarios"
        emptyMessage="Nenhum usuário encontrado."
        toolbar={
          <div className="flex flex-wrap items-end gap-3">
            <div className="w-56">
              <Label htmlFor="f-nome">Nome</Label>
              <Input
                id="f-nome"
                value={query.draft.nome ?? ""}
                onChange={(e) => query.setDraft((f) => ({ ...f, nome: e.target.value }))}
                onKeyDown={(e) => e.key === "Enter" && query.search()}
              />
            </div>
            <div className="w-64">
              <Label htmlFor="f-email">Email</Label>
              <Input
                id="f-email"
                value={query.draft.email ?? ""}
                onChange={(e) => query.setDraft((f) => ({ ...f, email: e.target.value }))}
                onKeyDown={(e) => e.key === "Enter" && query.search()}
              />
            </div>
            <Button onClick={query.search}>Buscar</Button>
          </div>
        }
      />
    </div>
  );
}
